import React from "react";
import { useQuery } from '@tanstack/react-query';
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import { getOpportunityUpdates } from "@/helpers/apis";
import { GetOpportunityUpdatesResponse } from "@/pages/explorer/types";
import { EmptyScreen } from "@/pages/warehouse/misc";
import TimelineEventIconWithDate from "./TimelineEventIcon";
import { timeline_contentStyle, iconStyle } from "./timelineStyles";
import styles from "@/pages/query/styles.module.scss";
import { Spinner, Stack } from "@altimateai/lego";

const GET_OPPORTUNITY_UPDATES = "GET_OPPORTUNITY_UPDATES";

const getOpportunityText = (data: GetOpportunityUpdatesResponse): string => {
  const { created_by, owner, status } = data;

  if (owner === "Unassigned") {
    if (created_by) {
      return "The opportunity was unassigned by " + created_by;
    } else {
      return "The opportunity was unassigned";
    }
  }

  if (created_by && owner && status) {
    return `${created_by} assigned the opportunity to ${owner} and changed the status to ${status}`;
  } else if (created_by && owner) {
    return `${created_by} assigned the opportunity to ${owner}`;
  } else if (created_by && status) {
    return `${created_by} changed the status to ${status}`;
  } else if (owner && status) {
    return `${owner} was assigned the opportunity and the status was changed to ${status}`;
  } else if (created_by) {
    return `${created_by} created the opportunity`;
  } else if (owner) {
    return `${owner} was assigned the opportunity`;
  } else if (status) {
    return `The status was changed to ${status}`;
  }

  return "";
};

interface OpportunityActivityProps {
  opportunity_id: string;
}

const OpportunityActivity: React.FC<OpportunityActivityProps> = ({ opportunity_id }) => {
  const { data, isLoading } = useQuery({
    queryKey: [GET_OPPORTUNITY_UPDATES, opportunity_id],
    queryFn: () => getOpportunityUpdates({ opportunity_id }),
  });

  if (isLoading)
    return (
      <Stack justify="center" align="center" className="al-h-40">
        <Spinner />
      </Stack>
    );

  if (!data || !data.length)
    return (
      <EmptyScreen
        title="No activity yet"
        subTitle="No activity found for the selected opportunity."
      />
    );

  return (
    <div id="opportunityEventsActivity" className={styles.opportunityActivity}>
      <VerticalTimeline
        className={styles.vertical_timeline}
        layout={"1-column-left"}
        lineColor={"#E7E8EA"}
      >
        {data.map(d => (
          <VerticalTimelineElement
            key={d.opportunity_id}
            className={styles.vertical_timeline_element}
            contentStyle={timeline_contentStyle}
            contentArrowStyle={{
              display: "none",
            }}
            iconStyle={iconStyle}
            icon={TimelineEventIconWithDate(
              d.owner || undefined,
              d.status || undefined,
              d.timestamp
            )}
          >
            {getOpportunityText(d)}
          </VerticalTimelineElement>
        ))}
      </VerticalTimeline>
    </div>
  );
};

export default OpportunityActivity;
